import React, { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import {
  Flex,
  FlexItem,
  RadioButtonGroup,
  RadioButton,
  styled,
  Text,
  Calendar,
} from '@erecruit/ui-kit';
import {
  Mode as CalendarMode,
  Modifier as CalendarModifier,
} from '@erecruit/ui-kit/src/molecules/Calendar/Calendar';
import { Intl } from '@erecruit/core';

import { getKey } from '../utilities';

const { FormattedMessage } = Intl;

const initialQuantity = 1;

const StyledRadioButton = styled(RadioButton)({
  height: '24px !important',
});

function areArraysSame(arr1: string[], arr2: string[]) {
  if (arr1.length !== arr2.length) {
    return false;
  }

  const lookup = arr1.reduce((acc, curr) => {
    acc[curr] = 0;
    return acc;
  }, {} as Record<string, number>);

  for (let i = 0; i < arr1.length; i++) {
    const item = arr2[i];
    if (lookup[item] === undefined) {
      return false;
    } else {
      lookup[item] = 1;
    }
  }

  const sum = Object.keys(lookup).reduce((total, curr) => {
    total += lookup[curr];
    return total;
  }, 0);

  return sum === arr1.length;
}

export const DatesPicker = ({
  selectedDates,
  selectedDatesQuantities,
  setSelectedDates,
  setSelectedDatesQuantities,
  selectionPerType,
  setSelectionPerType,
  calendarMode,
  setCalendarMode,
}: {
  selectedDates: Record<string, CalendarModifier>;
  selectedDatesQuantities: Record<string, number>;
  setSelectedDates: Dispatch<Record<string, CalendarModifier>>;
  setSelectedDatesQuantities: Dispatch<Record<string, number>>;
  selectionPerType: Record<CalendarMode, string[]>;
  setSelectionPerType: Dispatch<Record<CalendarMode, string[]>>;
  calendarMode: CalendarMode;
  setCalendarMode: Dispatch<SetStateAction<CalendarMode>>;
}) => {
  const datesPerSelection = useMemo(() => {
    const selectedItems = selectionPerType[calendarMode];
    const items = selectedItems.reduce((dates, key) => {
      dates.push(selectedDates[key]);
      return dates;
    }, [] as CalendarModifier[]);

    if (calendarMode === 'range') {
      if (selectedItems.length === 1) {
        return items[0];
      }
      return undefined;
    }

    return items;
  }, [calendarMode, selectionPerType, selectedDates]);

  const onCalendarChange = useCallback(
    (selected: CalendarModifier | CalendarModifier[]) => {
      if (selected === undefined) return;
      const newSelectionPerType = { ...selectionPerType };

      if (calendarMode === 'range') {
        const selection = selected as CalendarModifier;
        const key = getKey(selection);

        if (selectedDates[key]) {
          return;
        }

        const newSelectedDates = { ...selectedDates };
        const newSelectedDatesQuantities = { ...selectedDatesQuantities };

        const oldRangeKey = newSelectionPerType['range'][0];

        delete newSelectedDates[oldRangeKey];
        delete newSelectedDatesQuantities[oldRangeKey];

        newSelectionPerType['range'] = [key];
        newSelectedDates[key] = selection;
        newSelectedDatesQuantities[key] = initialQuantity;

        setSelectedDates(newSelectedDates);
        setSelectedDatesQuantities(newSelectedDatesQuantities);
        setSelectionPerType(newSelectionPerType);
      } else {
        const newSelection = selected as Date[];
        const newSelectionKeys = newSelection.map(selected => getKey(selected));
        if (areArraysSame(selectionPerType[calendarMode], newSelectionKeys)) {
          // no changes
          return;
        }

        const rangeSelectionKey =
          selectionPerType['range'] && selectionPerType['range'].length === 1
            ? selectionPerType['range'][0]
            : undefined;

        const newDatesSelection: Record<string, CalendarModifier> = {};
        const newDatesSelectionQuantities: Record<string, number> = {};

        if (rangeSelectionKey) {
          newDatesSelection[rangeSelectionKey] =
            selectedDates[rangeSelectionKey];
          newDatesSelectionQuantities[rangeSelectionKey] =
            selectedDatesQuantities[rangeSelectionKey];
        }

        newSelection.forEach(selected => {
          const key = getKey(selected);
          newDatesSelection[key] = selected;
          if (selectedDatesQuantities[key]) {
            newDatesSelectionQuantities[key] = selectedDatesQuantities[key];
          } else {
            newDatesSelectionQuantities[key] = initialQuantity;
          }
        });

        newSelectionPerType[calendarMode] = newSelectionKeys;
        setSelectionPerType(newSelectionPerType);
        setSelectedDates(newDatesSelection);
        setSelectedDatesQuantities(newDatesSelectionQuantities);
      }
    },
    [
      calendarMode,
      selectedDates,
      selectedDatesQuantities,
      setSelectedDatesQuantities,
      setSelectedDates,
      setSelectionPerType,
      selectionPerType,
    ],
  );

  return (
    <Flex direction="column" align="center">
      <FlexItem>
        <RadioButtonGroup
          defaultValue={calendarMode}
          onChange={e => setCalendarMode(e.target.value)}
        >
          <Flex gutter={3}>
            <StyledRadioButton value="multiple" key="multiple">
              <Text>
                <FormattedMessage
                  id={messageIds.vms.newShiftWizard.general.selection}
                />
              </Text>
            </StyledRadioButton>
            <StyledRadioButton value="range" key="range">
              <Text>
                <FormattedMessage
                  id={messageIds.vms.newShiftWizard.general.range}
                />
              </Text>
            </StyledRadioButton>
          </Flex>
        </RadioButtonGroup>
      </FlexItem>
      <FlexItem>
        <Calendar
          value={datesPerSelection}
          onChange={onCalendarChange}
          mode={calendarMode}
        />
      </FlexItem>
    </Flex>
  );
};
